import { formatCivilDate } from '@hue/design-system';
import type { CashCell, CashFlowReport, ReportFact } from './reports-api.js';

/// Formatos e contas da tela de relatórios, fora do componente para serem testados sozinhos.

const whole = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

/// A grade mostra reais inteiros: doze colunas de centavos não cabem. O detalhe mostra os centavos.
export function gridNumber(cents: number | null): string {
  if (cents === null) return '';
  if (Math.abs(cents) < 50) return '—';
  return `${cents < 0 ? '−' : ''}${whole.format(Math.abs(Math.round(cents / 100)))}`;
}

export function percent(ratio: number | null): string {
  if (ratio === null || !Number.isFinite(ratio)) return '';
  return `${ratio < 0 ? '−' : ''}${Math.abs(ratio * 100)
    .toFixed(1)
    .replace('.', ',')}%`;
}

/// "Só realizado": tira o previsto e as vencidas, e refaz os saldos só com o que já aconteceu.
export function realizedOnly(report: CashFlowReport): CashFlowReport {
  const opening = report.rows.find((row) => row.key === 'opening')!;
  const closing = report.rows.find((row) => row.key === 'closing')!;
  const generation = report.rows.find((row) => row.key === 'generation')!;
  // O ajuste de conta aberta no meio do ano é o que sobra entre os saldos e a geração.
  const adjustments = report.months.map(
    (_, index) =>
      closing.cells[index]!.realized -
      opening.cells[index]!.realized -
      generation.cells[index]!.realized -
      generation.cells[index]!.planned,
  );
  const openings: number[] = [];
  const closings: number[] = [];
  let balance = opening.cells[0]!.realized;
  report.months.forEach((_, index) => {
    openings.push(balance);
    balance += generation.cells[index]!.realized + adjustments[index]!;
    closings.push(balance);
  });
  const strip = (cells: CashCell[]) =>
    cells.map((cell) => ({ realized: cell.realized, planned: 0 }));
  const sumOf = (cells: CashCell[]) => ({
    realized: cells.reduce((total, cell) => total + cell.realized, 0),
    planned: 0,
  });
  return {
    ...report,
    rows: report.rows
      .filter((row) => !row.key.includes(':overdue'))
      .map((row) => {
        if (row.key === 'opening') {
          const cells = openings.map((value) => ({ realized: value, planned: 0 }));
          return { ...row, cells, total: cells[0]! };
        }
        if (row.key === 'closing') {
          const cells = closings.map((value) => ({ realized: value, planned: 0 }));
          return { ...row, cells, total: cells[11]! };
        }
        const cells = strip(row.cells);
        return { ...row, cells, total: sumOf(cells) };
      }),
    summary: {
      ...report.summary,
      balanceEndCents: closings[11]!,
      lowest: null,
    },
  };
}

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

export function factWhen(fact: ReportFact, today?: string): string {
  const date = formatCivilDate(fact.date);
  if (fact.origin === 'OBLIGATION' || fact.origin === 'RECURRENCE') {
    if (fact.status === 'OVERDUE' && today) {
      const late = daysBetween(fact.date, today);
      return `Venceu em ${date} · ${late} ${late === 1 ? 'dia' : 'dias'} de atraso`;
    }
    return `Vencimento ${date}`;
  }
  return `Extrato de ${date}`;
}
